import {
  Alert,
  Box,
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { CheckCircleOutline } from "@mui/icons-material";
import { useEffect, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { extractApiError } from "@/shared/api/apiClient";
import { ResponsiveDialog } from "@/shared/components/ResponsiveDialog";
import { PhoneField } from "@/shared/components/PhoneField";
import { schoolRegistrationApi, type SchoolRegistrationPayload } from "./schoolRegistrationApi";

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PHONE_REGEX = /^[0-9]{10}$/;

interface Props {
  open: boolean;
  onClose: () => void;
}

interface FormState {
  schoolName: string;
  managerName: string;
  managerPhone: string;
  managerEmail: string;
  schoolPhone: string;
  schoolWebsite: string;
}

const empty: FormState = {
  schoolName: "",
  managerName: "",
  managerPhone: "",
  managerEmail: "",
  schoolPhone: "",
  schoolWebsite: "",
};

export const SchoolRegistrationRequestDialog = ({ open, onClose }: Props) => {
  const [form, setForm] = useState<FormState>(empty);
  const [error, setError] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: (payload: SchoolRegistrationPayload) => schoolRegistrationApi.submit(payload),
    onError: (e) => setError(extractApiError(e)),
  });

  // Diyalog her açıldığında formu ve durumu sıfırla (onay ekranından sonra tekrar açılabilsin).
  useEffect(() => {
    if (open) {
      setForm(empty);
      setError(null);
      mutation.reset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const set =
    (key: keyof FormState) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
      setForm((f) => ({ ...f, [key]: e.target.value }));

  const submit = () => {
    setError(null);
    if (!form.schoolName.trim()) {
      setError("Lütfen okul adını giriniz");
      return;
    }
    if (!form.managerName.trim()) {
      setError("Lütfen yönetici ad soyadını giriniz");
      return;
    }
    if (!PHONE_REGEX.test(form.managerPhone)) {
      setError("Yönetici cep telefonu 10 haneli olmalıdır (sadece rakam)");
      return;
    }
    if (!EMAIL_REGEX.test(form.managerEmail)) {
      setError("Geçerli bir yönetici e-posta adresi giriniz");
      return;
    }
    mutation.mutate({
      schoolName: form.schoolName.trim(),
      managerName: form.managerName.trim(),
      managerPhone: form.managerPhone,
      managerEmail: form.managerEmail.trim(),
      schoolPhone: form.schoolPhone.trim() || undefined,
      schoolWebsite: form.schoolWebsite.trim() || undefined,
    });
  };

  const success = mutation.isSuccess;

  return (
    <ResponsiveDialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{success ? "Talebiniz Alındı" : "Okul Kaydı İçin Başvuru"}</DialogTitle>

      {success ? (
        <>
          <DialogContent>
            <Stack spacing={2} alignItems="center" sx={{ py: 2, textAlign: "center" }}>
              <CheckCircleOutline color="success" sx={{ fontSize: 56 }} />
              <Typography variant="body1">
                Talebiniz alınmıştır. Hesabınız en fazla 24 saat içinde aktifleştirilip size bir onay
                e-postası gönderilecektir.
              </Typography>
            </Stack>
          </DialogContent>
          <DialogActions sx={{ p: 2 }}>
            <Button variant="contained" onClick={onClose}>
              Kapat
            </Button>
          </DialogActions>
        </>
      ) : (
        <>
          <DialogContent>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Okulunuzu Zekabin'e kaydettirmek için aşağıdaki bilgileri iletin. Ekibimiz en kısa
              sürede sizinle iletişime geçecektir.
            </Typography>
            <Stack spacing={2} sx={{ mt: 0.5 }}>
              {error && <Alert severity="error">{error}</Alert>}
              <TextField
                label="Okul Adı"
                value={form.schoolName}
                onChange={set("schoolName")}
                fullWidth
                required
                autoFocus
              />
              <TextField
                label="Yönetici Ad Soyad"
                value={form.managerName}
                onChange={set("managerName")}
                fullWidth
                required
              />
              <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 2 }}>
                <PhoneField
                  label="Yönetici Telefonu"
                  value={form.managerPhone}
                  onChange={(v) => setForm((f) => ({ ...f, managerPhone: v }))}
                  fullWidth
                  required
                />
                <TextField
                  label="Yönetici E-posta"
                  type="email"
                  value={form.managerEmail}
                  onChange={set("managerEmail")}
                  fullWidth
                  required
                />
              </Box>
              <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 2 }}>
                <PhoneField
                  label="Okul Telefonu"
                  value={form.schoolPhone}
                  onChange={(v) => setForm((f) => ({ ...f, schoolPhone: v }))}
                  fullWidth
                  placeholder="(opsiyonel)"
                />
                <TextField
                  label="Okul Web Sitesi"
                  value={form.schoolWebsite}
                  onChange={set("schoolWebsite")}
                  fullWidth
                  placeholder="(opsiyonel)"
                />
              </Box>
            </Stack>
          </DialogContent>
          <DialogActions sx={{ p: 2 }}>
            <Button onClick={onClose} disabled={mutation.isPending}>
              Vazgeç
            </Button>
            <Button variant="contained" onClick={submit} disabled={mutation.isPending}>
              Talep Yap
            </Button>
          </DialogActions>
        </>
      )}
    </ResponsiveDialog>
  );
};
